import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
}

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function DatePicker({ value, onChange }: DatePickerProps) {
  const [excludeYear, setExcludeYear] = useState(true);
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');

  useEffect(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 2) {
        setMonth(parts[0]);
        setDay(parts[1]);
        setExcludeYear(true);
      } else if (parts.length === 3) {
        setMonth(parts[0]);
        setDay(parts[1]);
        setYear(parts[2]);
        setExcludeYear(false);
      }
    }
  }, []);

  const handleUpdate = (newMonth: string, newDay: string, newYear: string, newExcludeYear: boolean) => {
    if (newMonth && newDay) {
      if (newExcludeYear) {
        onChange(`${newMonth}-${newDay}`);
      } else if (newYear) {
        onChange(`${newMonth}-${newDay}-${newYear}`);
      }
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = e.target.value;
    setMonth(newMonth);
    handleUpdate(newMonth, day, year, excludeYear);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDay = e.target.value;
    setDay(newDay);
    handleUpdate(month, newDay, year, excludeYear);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newYear = e.target.value;
    setYear(newYear);
    handleUpdate(month, day, newYear, excludeYear);
  };

  const handleExcludeYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newExcludeYear = e.target.checked;
    setExcludeYear(newExcludeYear);
    handleUpdate(month, day, year, newExcludeYear);
  };

  const getDaysInMonth = () => {
    if (!month) return 31;
    const monthIndex = months.indexOf(month);
    if ([3, 5, 8, 10].includes(monthIndex)) return 30;
    if (monthIndex === 1) return 29;
    return 31;
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <div className="flex-1">
          <select
            value={month}
            onChange={handleMonthChange}
            className="bg-white/5 w-full px-4 py-3 border-2 border-gray-200/10 rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all appearance-none bg-white cursor-pointer"
          >
            <option value="">Month</option>
            {months.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <select
            value={day}
            onChange={handleDayChange}
            className="bg-white/5 w-full px-4 py-3 border-2 border-gray-200/10 rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all appearance-none bg-white cursor-pointer"
          >
            <option value="">Day</option>
            {Array.from({ length: getDaysInMonth() }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d.toString().padStart(2, '0')}>
                {d}
              </option>
            ))}
          </select>
        </div>
        {!excludeYear && (
          <div className="flex-1">
            <input
              type="number"
              value={year}
              onChange={handleYearChange}
              placeholder="Year"
              min="1900"
              max="2024"
              className="bg-white/5 w-full px-4 py-3 border-2 border-gray-200/10 rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
            />
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 cursor-pointer group">
        <input
          type="checkbox"
          checked={excludeYear}
          onChange={handleExcludeYearChange}
          className="w-5 h-5 rounded border-2 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-200 cursor-pointer"
        />
        <span className="text-sm text-gray-400 group-hover:text-gray-50 transition-colors">
          Exclude year
        </span>
      </label>
    </div>
  );
}
